/**
 * HIKNIGHT cart relation sync V18
 *
 * One cart request per quantity action:
 * - Main bike quantity changes sync free gifts.
 * - Main bike quantity changes sync linked paid accessories.
 * - Removing a main bike removes its linked gifts/accessories.
 * - Gift quantity/remove controls remain blocked.
 *
 * IMPORTANT: this replaces Concept cart-items.updateQuantity, so all relation
 * logic lives here instead of being split between cart.js and this file.
 */
(function () {
  const routes = (window.theme && theme.routes) || {};
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const updateUrl = routes.cart_update_url || `${root}cart/update.js`;

  function keyFromRow(row) {
    return String(row?.id || '')
      .replace(/^CartDrawer-Item-/, '')
      .replace(/^CartItem-/, '');
  }

  function allCartRows() {
    const drawerRows = [...document.querySelectorAll('[id^="CartDrawer-Item-"]')];
    if (drawerRows.length) return drawerRows;
    return [...document.querySelectorAll('[id^="CartItem-"]')];
  }

  function findLineElement(lineKey) {
    return document.getElementById(`CartDrawer-Item-${lineKey}`)
      || document.getElementById(`CartItem-${lineKey}`);
  }

  async function parseJsonResponse(response) {
    const text = await response.text();
    try {
      return text ? JSON.parse(text) : {};
    } catch (_) {
      return {
        status: response.status || 500,
        errors: response.status === 429
          ? 'Cart is temporarily busy. Please wait a moment and try again.'
          : 'Unable to update the cart. Please try again.'
      };
    }
  }

  function buildUpdates(line, quantity) {
    const row = findLineElement(line);
    const updates = { [line]: quantity };
    if (!row) return updates;

    // Gifts are controlled only by their parent bike.
    if (row.hasAttribute('data-gift-line')) return null;

    // Changing a linked paid accessory directly does not change the bike.
    if (row.hasAttribute('data-hk-linked-accessory')) return updates;

    const mainVariantId = String(row.getAttribute('data-variant-id') || '');
    if (!mainVariantId) return updates;

    for (const candidate of allCartRows()) {
      if (candidate === row) continue;
      const key = keyFromRow(candidate);
      if (!key || key === String(line)) continue;

      const giftFor = String(candidate.getAttribute('data-gift-for') || '');
      const linkedMain = String(candidate.getAttribute('data-hk-linked-main-variant') || '');

      if (candidate.hasAttribute('data-gift-line') && giftFor === mainVariantId) {
        updates[key] = quantity;
      }
      if (candidate.hasAttribute('data-hk-linked-accessory') && linkedMain === mainVariantId) {
        updates[key] = quantity;
      }
    }

    return updates;
  }

  function patchCartItems() {
    const CartItems = customElements.get('cart-items');
    if (!CartItems || CartItems.prototype.__hiknightRelationSyncV18) return;
    CartItems.prototype.__hiknightRelationSyncV18 = true;

    CartItems.prototype.updateQuantity = function (line, quantity, name, target) {
      const updates = buildUpdates(line, quantity);
      if (updates === null) {
        this.disableLoading?.(line);
        return;
      }

      const signature = JSON.stringify(updates);
      if (this.__hiknightCartMutationSignature === signature) return;
      this.__hiknightCartMutationSignature = signature;
      this.enableLoading(line);

      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {
        bubbles: true,
        detail: { sections }
      }));

      fetch(updateUrl, {
        ...theme.utils.fetchConfig(),
        body: JSON.stringify({
          updates,
          sections,
          sections_url: window.location.pathname
        })
      })
        .then(parseJsonResponse)
        .then((parsedState) => {
          if (parsedState?.status || parsedState?.errors) {
            throw new Error(parsedState.errors || parsedState.description || 'Unable to update the cart.');
          }
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-items',
            cart: parsedState,
            target,
            line,
            name
          });
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') console.error(error);
          this.disableLoading?.(line);
        })
        .finally(() => {
          if (this.__hiknightCartMutationSignature === signature) {
            this.__hiknightCartMutationSignature = '';
          }
        });
    };
  }

  if (customElements.get('cart-items')) patchCartItems();
  else customElements.whenDefined('cart-items').then(patchCartItems);
})();
